import React, { useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Typography, Alert, List, ListItem, ListItemText, Box,
} from '@mui/material';

const bulkCreateFinancialSupportRegistrants = httpsCallable(functions, 'bulkCreateFinancialSupportRegistrants');

// Matches the fields bulkCreateFinancialSupportRegistrants accepts (see
// functions/index.js) - deliberately excludes financialSupportAmount/
// products, which are always computed server-side from participation/status
// + the conference's own pricing, so the template never goes stale against a
// price change in Conference Settings.
const CSV_HEADERS = [
  'firstName', 'lastName', 'email', 'phoneNumber', 'gender', 'status', 'participation', 'school', 'city', 'locality',
  'firstName_emergency', 'lastName_emergency', 'email_emergency', 'phoneNumber_emergency', 'isMinor',
  'signedWaiver', 'signedWaiverCamp', 'firstTime', 'hospitality', 'services', 'allergies', 'shirtsize',
  'financialSupportSource', 'servingOneName', 'servingOneEmail', 'comments',
];

// Matches BULK_UPLOAD_PARTICIPATION_KEYS in functions/index.js. Leave the
// "participation" column blank to derive full-time student/non-student
// pricing from status automatically (the original behavior, before this
// column existed) - only set it explicitly for a part-time link type.
const PARTICIPATION_OPTIONS = ['FT-STUDENT', 'FT-NONSTUDENT', 'SAT-AFTERNOON', 'SAT-EVENING', 'SATURDAY-ONLY', 'LORDS-DAY'];

// allergies is a fixed app-wide list (see allergyOptions in
// InformationForm.js) - services is NOT: it's whatever this conference has
// configured under Conference Settings > Service options, so the second
// example row's values are illustrative only and won't match every
// conference. Both are comma-separated when a registrant has more than one.
const ALLERGY_OPTIONS = ['dairy free', 'gluten free', 'nut free', 'vegetarian'];

// Two rows: the first shows the minimal case (no services/allergies), the
// second shows the comma-separated multi-value format for both columns.
const EXAMPLE_ROWS = [
  {
    firstName: 'Jane', lastName: 'Doe', email: 'jane.doe@example.com', phoneNumber: '555-123-4567',
    // participation left blank - derived automatically as FT-STUDENT from status.
    gender: 'F', status: 'Freshman', participation: '', school: 'UC Berkeley', city: '', locality: '',
    firstName_emergency: 'John', lastName_emergency: 'Doe', email_emergency: 'john.doe@example.com',
    phoneNumber_emergency: '555-987-6543', isMinor: 'FALSE',
    signedWaiver: 'TRUE', signedWaiverCamp: 'FALSE', firstTime: 'TRUE', hospitality: 'FALSE',
    services: '', allergies: '', shirtsize: 'M',
    financialSupportSource: 'First Baptist Church', servingOneName: '', servingOneEmail: '',
    comments: '',
  },
  {
    firstName: 'Marcus', lastName: 'Johnson', email: 'marcus.johnson@example.com', phoneNumber: '555-234-5678',
    // participation set explicitly, overriding what status would otherwise derive.
    gender: 'M', status: 'Sophomore', participation: 'SATURDAY-ONLY', school: 'San Jose State', city: '', locality: '',
    firstName_emergency: 'Diane', lastName_emergency: 'Johnson', email_emergency: 'diane.johnson@example.com',
    phoneNumber_emergency: '555-876-5432', isMinor: 'FALSE',
    signedWaiver: 'TRUE', signedWaiverCamp: 'FALSE', firstTime: 'FALSE', hospitality: 'TRUE',
    // Multiple values are comma-separated - each must exactly match one of
    // this conference's configured Service options (see Conference
    // Settings). Allergies must exactly match one of ALLERGY_OPTIONS above.
    services: 'Media / Social Media, Recreation and Activities',
    allergies: 'gluten free, nut free',
    shirtsize: 'L',
    financialSupportSource: 'Grace Fellowship', servingOneName: '', servingOneEmail: '',
    comments: '',
  },
];

function csvEscape(value) {
  const s = String(value ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadTemplate() {
  const lines = [
    CSV_HEADERS.join(','),
    ...EXAMPLE_ROWS.map((row) => CSV_HEADERS.map((h) => csvEscape(row[h] ?? '')).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'full-financial-support-registrants-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// Minimal RFC-4180-ish CSV parser: handles quoted fields (with embedded
// commas/newlines) and "" as an escaped quote - good enough for a template
// filled out in Excel/Sheets, not a general-purpose CSV library.
function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const pushField = () => { row.push(field); field = ''; };
  const pushRow = () => { rows.push(row); row = []; };

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      pushField();
    } else if (c === '\n') {
      pushField(); pushRow();
    } else if (c === '\r') {
      // skip - the following \n handles the row break
    } else {
      field += c;
    }
  }
  if (field.length > 0 || row.length > 0) { pushField(); pushRow(); }

  const nonEmptyRows = rows.filter((r) => r.some((cell) => cell.trim() !== ''));
  if (nonEmptyRows.length === 0) return [];
  const headers = nonEmptyRows[0].map((h) => h.trim());
  return nonEmptyRows.slice(1).map((cells) => {
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = cells[idx] !== undefined ? cells[idx].trim() : ''; });
    return obj;
  });
}

// ADMIN/OWNER-only tool (gated by the caller - see canBulkUpload in
// AdminPage.js/EditRegistrantsPanel.js) for registering a batch of
// registrants whose entire cost is covered by their locality/church, with
// no PayPal step. See bulkCreateFinancialSupportRegistrants in
// functions/index.js for what each CSV column becomes and what's computed
// automatically (locality, registration cost, financial support amount).
export default function BulkUploadDialog({ conferenceId, legacyCollection, onUploaded }) {
  const fileInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [result, setResult] = useState(null);

  function reset() {
    setFileName('');
    setParsedRows([]);
    setParseError('');
    setResult(null);
  }

  function handleOpen() {
    reset();
    setOpen(true);
  }

  function handleClose() {
    if (!busy) setOpen(false);
  }

  function handlePickFile() {
    fileInputRef.current?.click();
  }

  function handleFileSelected(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    reset();
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = parseCsv(String(reader.result || ''));
        if (rows.length === 0) {
          setParseError('No data rows found in that file.');
          return;
        }
        setParsedRows(rows);
      } catch (error) {
        setParseError(`Could not read that CSV: ${error.message}`);
      }
    };
    reader.onerror = () => setParseError('Could not read that file.');
    reader.readAsText(file);
  }

  async function handleUpload() {
    setBusy(true);
    setResult(null);
    try {
      const params = legacyCollection
        ? { collection: legacyCollection, rows: parsedRows }
        : { conferenceId, rows: parsedRows };
      const res = await bulkCreateFinancialSupportRegistrants(params);
      setResult(res.data);
      if (res.data?.created?.length > 0) onUploaded?.();
    } catch (error) {
      console.error('Bulk upload failed:', error);
      setResult({ created: [], errors: [{ row: '-', error: error.message }] });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button variant="outlined" size="small" onClick={handleOpen}>
        Bulk upload (full financial support)
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk upload registrants with full financial support</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            For registrants whose entire cost is covered by their locality/church - created directly, with no PayPal
            step. Each row is marked as having full financial support (they owe nothing); their resolved registration
            cost is still recorded so it shows up correctly in Payment View. Applies to the conference currently
            selected above.
          </DialogContentText>
          <DialogContentText variant="body2" sx={{ mb: 2 }}>
            <strong>participation</strong> is optional - leave it blank to derive full-time student/non-student
            pricing from status automatically, or set it to one of: {PARTICIPATION_OPTIONS.join(', ')} for a
            part-time link type.
          </DialogContentText>
          <DialogContentText variant="body2" sx={{ mb: 2 }}>
            <strong>services</strong> and <strong>allergies</strong> accept more than one value, comma-separated.
            Allergies must exactly match one of: {ALLERGY_OPTIONS.join(', ')}. Services must exactly match this
            conference's own configured Service options (see Conference Settings) - the template's example is
            illustrative only.
          </DialogContentText>

          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" onClick={downloadTemplate}>Download CSV template</Button>
            <Button variant="contained" size="small" onClick={handlePickFile} disabled={busy}>Choose CSV file...</Button>
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleFileSelected} />
          </Box>

          {fileName && !parseError && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              {fileName}: {parsedRows.length} row{parsedRows.length === 1 ? '' : 's'} ready to upload.
            </Typography>
          )}
          {parseError && <Alert severity="error" sx={{ mb: 2 }}>{parseError}</Alert>}

          {result && (
            <Box sx={{ mt: 2 }}>
              {result.created.length > 0 && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  Created {result.created.length} registrant{result.created.length === 1 ? '' : 's'}.
                </Alert>
              )}
              {result.errors.length > 0 && (
                <>
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {result.errors.length} row{result.errors.length === 1 ? '' : 's'} skipped:
                  </Alert>
                  <List dense>
                    {result.errors.map((e, i) => (
                      <ListItem key={i} disableGutters>
                        <ListItemText primary={`Row ${e.row}${e.email ? ` (${e.email})` : ''}`} secondary={e.error} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={busy}>Close</Button>
          <Button
            variant="contained" onClick={handleUpload}
            disabled={busy || parsedRows.length === 0 || !!parseError}
          >
            {busy ? 'Uploading...' : `Upload ${parsedRows.length || ''} row${parsedRows.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
